import { borderRadius, boxShadows, spaces } from '@kimuno/theme';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Button,
	type ButtonOwnProps,
	DialogContent,
	DialogTitle,
	IconButton,
	Dialog as MuiDialog,
} from '@mui/material';
import type React from 'react';

import { shouldDisplayHintsAndOnboardingDialogs } from '../../utils/shouldDisplayHintsAndOnboardingDialogs';
import useScreenSize from '../../utils/useScreenSize';

export interface DialogProps {
	title: string;
	image?: string;
	videoEmbed?: {
		src: string;
		aspectRatio?: string;
	};
	imageStyles?: React.CSSProperties;
	primaryCta: {
		size?: 'small' | 'medium' | 'large';
		label: string;
		onClick: () => void;
		color?: ButtonOwnProps['color'];
	};
	secondaryCta?: {
		variant?: 'contained' | 'outlined' | 'text';
		size?: 'small' | 'medium' | 'large';
		label: string;
		onClick: () => void;
		color?: ButtonOwnProps['color'];
	};
	onClose?: () => void;
	open: boolean;
	htmlBody?: React.ReactNode;
	position?: 'center' | 'right-bottom';
	desktopWidth?: number;
	desktopHeight?: number;
	mobileWidth?: number;
	dataTestId?: string;
	zIndex?: number;
	noTitle?: boolean;
}

const Dialog: React.FC<DialogProps> = ({ position = 'center', ...props }) => {
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isMobile = isXsScreen || isSmScreen;
	const isLocatedBottom: boolean = position === 'right-bottom';

	const onClose = (_event: object, reason: string) => {
		if (reason === 'backdropClick') {
			return;
		}
		props.onClose?.();
	};

	return (
		<MuiDialog
			data-testid={props.dataTestId}
			open={props.open && shouldDisplayHintsAndOnboardingDialogs()}
			onClose={onClose}
			hideBackdrop={isLocatedBottom}
			disableEnforceFocus={isLocatedBottom}
			disableScrollLock={isLocatedBottom}
			PaperProps={{
				sx: (theme) => {
					let styles = {
						boxShadow: boxShadows.elevation6,
						[theme.breakpoints.up('mobile')]: {
							width: props.desktopWidth || '600px',
						},
						[theme.breakpoints.down('mobile')]: {
							width: props.mobileWidth || '343px',
						},
					};

					if (isLocatedBottom) {
						styles = {
							...styles,
							position: 'fixed',
							right: '0px',
							bottom: spaces.xxxl,
							pointerEvents: 'all',
						};
					}

					return styles;
				},
			}}
			sx={{
				...(isLocatedBottom
					? {
							pointerEvents: 'none!important',
							'& .MuiDialogContent-root, & .MuiDialogTitle-root, & .MuiDialogActions-root': {
								pointerEvents: 'all!important',
							},
						}
					: {}),
				...(props.zIndex ? { zIndex: props.zIndex } : {}),
			}}
		>
			{!props.noTitle && (
				<DialogTitle
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						paddingX: spaces.lg,
						paddingY: isMobile ? spaces.md : spaces.lg,
						gap: spaces.md,
					}}
					variant={isMobile ? 'h6' : 'h5'}
				>
					{props.title}
					<IconButton
						color="secondary"
						aria-label="close"
						data-testid="dialog-dismiss-button"
						onClick={props.onClose}
						sx={{
							padding: spaces.xxs,
							pointerEvents: 'all',
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
			)}
			{(props.image || props.videoEmbed) && (
				<DialogContent
					sx={(theme) => {
						return {
							paddingBottom: spaces.md,
							[theme.breakpoints.down('mobile')]: {
								paddingTop: 0,
								paddingBottom: 0,
							},
						};
					}}
				>
					{props.image && (
						<Box
							component="img"
							alt={props.title}
							src={props.image}
							sx={(theme) => {
								return {
									width: '1',
									[theme.breakpoints.up('mobile')]: {
										aspectRatio: '16/9',
									},
									[theme.breakpoints.down('mobile')]: {
										aspectRatio: '4/3',
									},
									marginBottom: spaces.md,
									borderRadius: borderRadius.xs,
									...props.imageStyles,
								};
							}}
						/>
					)}
					{props.videoEmbed && (
						<Box
							sx={{
								position: 'relative',
								aspectRatio: '16/9',
							}}
						>
							<iframe
								src={props.videoEmbed.src}
								title={props.title}
								allowFullScreen
								style={{
									position: 'absolute',
									border: 0,
									borderRadius: borderRadius.xs,
									top: 0,
									left: 0,
									width: '100%',
									height: '100%',
								}}
							/>
						</Box>
					)}
				</DialogContent>
			)}
			{props.htmlBody && (
				<DialogContent
					sx={{
						paddingTop: 0,
						paddingBottom: isLocatedBottom ? spaces.md : 0,
					}}
				>
					{props.htmlBody}
				</DialogContent>
			)}
			<DialogContent
				sx={(theme) => {
					return {
						display: 'flex',
						padding: spaces.lg,
						[theme.breakpoints.up('tablet')]: {
							justifyContent: 'flex-end',
						},
						[theme.breakpoints.between('mobile', 'tablet')]: {
							justifyContent: 'space-between',
							'& > button': {
								flexGrow: 1,
							},
						},
						[theme.breakpoints.down('mobile')]: {
							flexDirection: 'column-reverse',
							padding: spaces.md,
						},
					};
				}}
			>
				{props.secondaryCta && (
					<Button
						variant={props.secondaryCta.variant || 'outlined'}
						size={props.secondaryCta.size || 'large'}
						data-testid="dialog-secondary-cta-button"
						onClick={props.secondaryCta.onClick}
						color={props.secondaryCta.color}
						sx={(theme) => {
							return {
								[theme.breakpoints.up('mobile')]: {
									marginRight: spaces.xs,
								},
								[theme.breakpoints.down('mobile')]: {
									marginTop: spaces.xs,
								},
								pointerEvents: 'all',
							};
						}}
					>
						{props.secondaryCta.label}
					</Button>
				)}
				<Button
					variant="contained"
					size={props.primaryCta.size || 'large'}
					data-testid="dialog-primary-cta-button"
					onClick={props.primaryCta.onClick}
					color={props.primaryCta.color}
					sx={{
						pointerEvents: 'all',
					}}
				>
					{props.primaryCta.label}
				</Button>
			</DialogContent>
		</MuiDialog>
	);
};

export default Dialog;
